import { useEffect, useMemo, useState } from 'react';
import { useSyncExternalStore } from 'react';
import * as Y from 'yjs';
import { Awareness } from 'y-protocols/awareness';
import { useNostr } from '@nostrify/react';

import { DOC_RELAYS } from '@/lib/doc/kinds';
import { NostrDocProvider, type ProviderState } from '@/lib/doc/provider';
import { touchRecent } from '@/lib/doc/storage';
import type { PresenceUser } from '@/lib/doc/types';
import { colorForPubkey, sanitizeUrl, shortPubkey } from '@/lib/identity';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';

export interface UseDocSyncResult {
  ydoc: Y.Doc;
  awareness: Awareness;
  provider: NostrDocProvider;
  state: ProviderState;
  presenceUsers: PresenceUser[];
}

/**
 * Open a document: creates the Yjs doc + awareness + sync provider,
 * keeps presence identity in sync with the logged-in user's profile,
 * and tears everything down on unmount.
 */
export function useDocSync(ownerPubkey: string, docId: string): UseDocSyncResult {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const author = useAuthor(user?.pubkey);

  // One provider instance per document. The editor page is keyed by
  // document, so these never need to be recreated while mounted.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable per document
  const { ydoc, awareness, provider } = useMemo(() => {
    const ydoc = new Y.Doc();
    const awareness = new Awareness(ydoc);
    const provider = new NostrDocProvider({
      ownerPubkey,
      docId,
      nostr: nostr.group([...DOC_RELAYS]),
      signer: user?.signer,
      ydoc,
      awareness,
    });
    return { ydoc, awareness, provider };
  }, [ownerPubkey, docId, nostr]);

  useEffect(() => {
    void provider.start();
    return () => provider.destroy();
  }, [provider]);

  // React to login/logout while the document is open.
  useEffect(() => {
    provider.setSigner(user?.signer);
  }, [provider, user?.signer]);

  const state = useSyncExternalStore(
    (onStoreChange) => provider.subscribe(onStoreChange),
    () => provider.getState(),
    () => provider.getState(),
  );

  // Publish our identity into the awareness state (used by remote carets
  // and the presence avatars in the header).
  useEffect(() => {
    const pubkey = user?.pubkey;
    if (!pubkey) return;
    const metadata = author.data?.metadata;
    const name = metadata?.display_name || metadata?.name || shortPubkey(pubkey);
    const picture = sanitizeUrl(metadata?.picture);
    awareness.setLocalStateField('user', { pubkey, name, color: colorForPubkey(pubkey), picture });
  }, [awareness, user?.pubkey, author.data]);

  // Track present users for the header avatars.
  const [presenceUsers, setPresenceUsers] = useState<PresenceUser[]>([]);
  useEffect(() => {
    const update = () => setPresenceUsers(provider.getPresenceUsers());
    update();
    awareness.on('change', update);
    const interval = setInterval(update, 5000);
    return () => {
      awareness.off('change', update);
      clearInterval(interval);
    };
  }, [provider, awareness]);

  // Record recently opened documents for the dashboard.
  useEffect(() => {
    if (user?.pubkey) {
      touchRecent(user.pubkey, ownerPubkey, docId);
    }
  }, [user?.pubkey, ownerPubkey, docId]);

  return { ydoc, awareness, provider, state, presenceUsers };
}
